if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.navigation = this.querySelector('[data-product-media-navigation]');
      this.previousButton = this.querySelector('[data-product-media-previous]');
      this.nextButton = this.querySelector('[data-product-media-next]');
      this.counter = this.querySelector('[data-product-media-counter]');
      this.touchStart = null;
      this.isAnimating = false;
      this.transitionTimer = null;

      if (this.navigation && this.previousButton && this.nextButton) {
        this.previousButton.addEventListener('click', () => this.navigate(-1));
        this.nextButton.addEventListener('click', () => this.navigate(1));
        this.navigation.addEventListener('pointerup', (event) => event.stopPropagation());
      }

      this.addEventListener('keydown', (event) => this.onKeydown(event));

      const content = this.querySelector('.product-media-modal__content');
      if (content) {
        content.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
        content.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
        content.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
        content.addEventListener('touchcancel', () => this.onTouchCancel(), { passive: true });
      }
    }

    hide() {
      super.hide();
      this.cancelTransition();
    }

    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }

    showActiveMedia() {
      const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`);
      this.setActiveMedia(activeMedia);
    }

    getMediaItems() {
      return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
    }

    setActiveMedia(activeMedia) {
      if (!activeMedia) return;

      this.getMediaItems().forEach((element) => element.classList.remove('active'));
      this.resetMediaStyles(activeMedia);

      const activeMediaTemplate = activeMedia.querySelector('template');
      const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
      activeMedia.classList.add('active');
      activeMedia.scrollIntoView({ block: 'center', inline: 'center' });

      const container = this.querySelector('[role="document"]');
      container.scrollLeft = (activeMedia.getBoundingClientRect().width - container.clientWidth) / 2;

      if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube'))
        activeMedia.loadContent();

      this.updateNavigation(activeMedia);
    }

    navigate(step) {
      const mediaItems = this.getMediaItems();
      if (mediaItems.length < 2 || this.isAnimating) return;

      const activeIndex = mediaItems.findIndex((element) => element.classList.contains('active'));
      if (activeIndex < 0) return;

      const nextIndex = (activeIndex + step + mediaItems.length) % mediaItems.length;
      window.pauseAllMedia();

      if (this.shouldAnimate()) {
        this.animateNavigation(mediaItems[activeIndex], mediaItems[nextIndex], step);
      } else {
        this.setActiveMedia(mediaItems[nextIndex]);
      }
    }

    shouldAnimate() {
      return window.matchMedia('(max-width: 749px)').matches &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    animateNavigation(activeMedia, nextMedia, step) {
      this.isAnimating = true;
      activeMedia.classList.remove('hd-media-is-dragging', 'hd-media-swipe-return');
      activeMedia.classList.add(step > 0 ? 'hd-media-swipe-out-left' : 'hd-media-swipe-out-right');

      this.transitionTimer = window.setTimeout(() => {
        this.resetMediaStyles(activeMedia);
        this.setActiveMedia(nextMedia);
        nextMedia.classList.add(step > 0 ? 'hd-media-swipe-in-right' : 'hd-media-swipe-in-left');

        this.transitionTimer = window.setTimeout(() => {
          this.resetMediaStyles(nextMedia);
          this.isAnimating = false;
          this.transitionTimer = null;
        }, 220);
      }, 150);
    }

    returnActiveMedia(activeMedia) {
      if (!activeMedia) return;

      activeMedia.classList.remove('hd-media-is-dragging');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.resetMediaStyles(activeMedia);
        return;
      }

      this.isAnimating = true;
      activeMedia.classList.add('hd-media-swipe-return');
      this.transitionTimer = window.setTimeout(() => {
        this.resetMediaStyles(activeMedia);
        this.isAnimating = false;
        this.transitionTimer = null;
      }, 200);
    }

    resetMediaStyles(media) {
      if (!media) return;

      media.classList.remove(
        'hd-media-is-dragging',
        'hd-media-swipe-return',
        'hd-media-swipe-out-left',
        'hd-media-swipe-out-right',
        'hd-media-swipe-in-left',
        'hd-media-swipe-in-right'
      );
      media.style.removeProperty('--hd-media-drag-x');
      media.style.removeProperty('--hd-media-drag-opacity');
    }

    cancelTransition() {
      if (this.transitionTimer) window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
      this.touchStart = null;
      this.isAnimating = false;
      this.getMediaItems().forEach((media) => this.resetMediaStyles(media));
    }

    updateNavigation(activeMedia) {
      if (!this.navigation) return;

      const mediaItems = this.getMediaItems();
      this.navigation.hidden = mediaItems.length < 2;

      if (this.counter && mediaItems.length > 1) {
        const activeIndex = mediaItems.indexOf(activeMedia);
        this.counter.textContent = `${activeIndex + 1} / ${mediaItems.length}`;
      }
    }

    onKeydown(event) {
      if (!this.hasAttribute('open') || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest('input, select, textarea, video, model-viewer')) return;

      const isRtl = getComputedStyle(this).direction === 'rtl';
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigate(isRtl ? 1 : -1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigate(isRtl ? -1 : 1);
      }
    }

    onTouchStart(event) {
      if (event.touches.length !== 1 || this.isAnimating || this.getMediaItems().length < 2) {
        this.touchStart = null;
        return;
      }

      const activeMedia = this.getMediaItems().find((element) => element.classList.contains('active'));
      if (!activeMedia) return;

      this.touchStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        time: Date.now(),
        axis: null,
        activeMedia: activeMedia
      };
    }

    onTouchMove(event) {
      if (!this.touchStart || this.isAnimating || event.touches.length !== 1) return;

      const deltaX = event.touches[0].clientX - this.touchStart.x;
      const deltaY = event.touches[0].clientY - this.touchStart.y;

      if (!this.touchStart.axis && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 8) {
        this.touchStart.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      }

      if (this.touchStart.axis !== 'horizontal') return;

      event.preventDefault();
      const viewportWidth = window.innerWidth || 375;
      const maximumDrag = viewportWidth * 0.7;
      const draggedX = Math.max(-maximumDrag, Math.min(maximumDrag, deltaX));
      const opacity = Math.max(0.68, 1 - (Math.abs(draggedX) / viewportWidth) * 0.4);
      const activeMedia = this.touchStart.activeMedia;
      activeMedia.style.setProperty('--hd-media-drag-x', `${draggedX}px`);
      activeMedia.style.setProperty('--hd-media-drag-opacity', opacity.toFixed(3));
      activeMedia.classList.add('hd-media-is-dragging');
    }

    onTouchEnd(event) {
      if (!this.touchStart || event.changedTouches.length !== 1) return;

      const touchState = this.touchStart;
      const deltaX = event.changedTouches[0].clientX - touchState.x;
      const deltaY = event.changedTouches[0].clientY - touchState.y;
      const elapsed = Date.now() - touchState.time;
      this.touchStart = null;

      if (touchState.axis !== 'horizontal' || Math.abs(deltaX) <= Math.abs(deltaY)) {
        this.resetMediaStyles(touchState.activeMedia);
        return;
      }

      const viewportWidth = window.innerWidth || 375;
      const distanceThreshold = Math.min(58, viewportWidth * 0.16);
      const isQuickFlick = elapsed < 350 && Math.abs(deltaX) >= 28;

      if (Math.abs(deltaX) >= distanceThreshold || isQuickFlick) {
        this.navigate(deltaX < 0 ? 1 : -1);
      } else {
        this.returnActiveMedia(touchState.activeMedia);
      }
    }

    onTouchCancel() {
      const activeMedia = this.touchStart ? this.touchStart.activeMedia : null;
      this.touchStart = null;
      this.resetMediaStyles(activeMedia);
    }
  });
}
